"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { computeMarketStats, MarketStats, ScannerMarketRow } from "@/lib/marketStats";

const TOP_MOVER_COUNT = 7;

type MarketIntelligenceDriver = {
  symbol: string;
  company: string;
  weight_pct: number;
  return_pct: number;
  contribution_pct: number;
};

type MarketIntelligenceSection = {
  title: string;
  lines: string[];
};

type MarketIntelligencePayload = {
  ok: boolean;
  index: string;
  available_indices: string[];
  source: {
    constituents_file: string;
    constituents_file_date: string | null;
    latest_session_date: string;
  };
  fx: {
    usd_pkr: number;
    source: string;
    kind: string;
  };
  selected_index: {
    code: string;
    level: number | null;
    daily_pct: number | null;
    session_date: string | null;
    source_file: string | null;
    level_kind: string;
    advancers_count: number;
    decliners_count: number;
    unchanged_count: number;
    equal_weight_return_pct: number | null;
    cap_weight_return_pct: number | null;
    top5_weight_concentration_pct: number | null;
    total_traded_value_pkr: number;
    total_traded_value_usd: number | null;
    previous_total_traded_value_pkr: number;
    previous_total_traded_value_usd: number | null;
    traded_value_change_pct: number | null;
    traded_value_trend: "increasing" | "decreasing" | "flat" | "unknown";
    traded_value_series_30d: {
      session_date: string;
      traded_value_pkr: number;
      traded_value_usd: number | null;
      symbols_with_data: number;
    }[];
    total_market_cap_pkr: number;
    total_market_cap_usd: number | null;
    total_ff_market_cap_pkr: number;
    total_ff_market_cap_usd: number | null;
    constituents_with_data: number;
    constituent_count: number;
  };
  inputs: {
    advancers_count: number;
    decliners_count: number;
    unchanged_count: number;
    top_weighted_contributors: MarketIntelligenceDriver[];
    top_weighted_detractors: MarketIntelligenceDriver[];
    equal_weight_index_return_pct: number | null;
    cap_weight_return_pct: number | null;
    top5_weight_concentration_pct: number | null;
    total_traded_value: number;
    constituents_with_data: number;
    constituent_count: number;
  };
  sections: MarketIntelligenceSection[];
  structuredMarketIntelligence: {
    marketSnapshot: {
      indexName: string;
      level: number;
      dailyChange: number;
      sessionDate: string;
    };
    marketHealth: {
      score: number;
      regimeLabel: string;
      interpretation: string;
    };
    participation: {
      participationPct: number;
      breadthDirection: "positive" | "negative" | "neutral";
    };
    weightComparison: {
      capReturn: number;
      equalReturn: number;
      divergence: number;
      status: string;
    };
    topContributors: { ticker: string; impact: number }[];
    topDetractors: { ticker: string; impact: number }[];
    driverConcentrationImpact: {
      top3Impact: number;
      totalMove: number;
      impactSharePct: number;
      driverType: "contributors" | "detractors" | "mixed";
      distributionLabel: "concentrated" | "moderate" | "broad";
    };
    concentration: {
      top5Weight: number;
      vs3moAvg: number;
    };
    liquidity: {
      tradedValue: number;
      vs30dAvgPct: number;
    };
    structuralText: string;
  };
};

type RelativeStrengthPayload = {
  indexName: string;
  benchmarkName: string;
  period: {
    from: string;
    to: string;
    daysUsed: number;
    daysDropped: number;
  };
  cumulative: {
    selected_3M: number;
    benchmark_3M: number;
    RS_3M: number;
  };
  windows: {
    RS_30D: number | null;
    RS_60D: number | null;
    RS_90D: number | null;
  };
  state: string;
  rsLine: { date: string; value: number }[];
  notes: string[];
};

type RelativeStrengthPeersPayload = {
  index: string;
  generated_at?: string | null;
  pairs: RelativeStrengthPayload[];
};

type LiveIndex = {
  index: string;
  code: string;
  value: number | null;
  change: number | null;
  pct_change: number | null;
};

type LivePayload = {
  source_url: string;
  as_of: string | null;
  indices: LiveIndex[];
};


const MARKET_INTELLIGENCE_INDICES = [
  "KSE-ALL-SHARES",
  "KSE-100",
  "KMI-30",
  "MII30",
  "PSXDIV20",
];

const INDEX_LABELS: Record<string, string> = {
  "KSE-ALL-SHARES": "KSE-ALL",
  "KSE-100": "KSE-100",
  "KMI-30": "KMI-30",
  MII30: "MII30",
  "PSXDIV20": "PSXDIV20",
};

const INDEX_CODE_MAP: Record<string, string> = {
  "KSE-ALL-SHARES": "ALLSHR",
  "KSE-100": "KSE100",
  "KMI-30": "KMI30",
  MII30: "MII30",
  PSXDIV20: "PSXDIV20",
};

const normalizeCode = (value: string) => value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

const INDEX_ALIASES: Record<string, string> = {
  ALLSHR: "KSE-ALL-SHARES",
  KSEALL: "KSE-ALL-SHARES",
  "KSE-ALL": "KSE-ALL-SHARES",
  KSEALLSHARES: "KSE-ALL-SHARES",
  "KSE-ALL-SHARES": "KSE-ALL-SHARES",
  KSE100: "KSE-100",
  "KSE-100": "KSE-100",
  KMI30: "KMI-30",
  "KMI-30": "KMI-30",
  MII30: "MII30",
  PSXDIV20: "PSXDIV20",
};

const normalizeIndexSelection = (value: string | null | undefined) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const alias =
    INDEX_ALIASES[trimmed.toUpperCase()] ||
    INDEX_ALIASES[normalizeCode(trimmed)] ||
    trimmed.toUpperCase();
  return MARKET_INTELLIGENCE_INDICES.includes(alias) ? alias : null;
};

const parseMaybeNumber = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const normalizeLivePayload = (raw: unknown): LivePayload | null => {
  if (!raw || typeof raw !== "object") return null;
  const payload = raw as Record<string, unknown>;
  const rawIndices = Array.isArray(payload.indices) ? payload.indices : [];
  const indices: LiveIndex[] = rawIndices
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const index = String(row.index ?? row.name ?? row.code ?? "").trim();
      const code = String(row.code ?? row.index ?? row.name ?? "").trim();
      if (!index && !code) return null;
      return {
        index,
        code,
        value: parseMaybeNumber(row.value ?? row.index_value ?? row.current_value ?? row.level),
        change: parseMaybeNumber(row.change ?? row.point_change ?? row.net_change),
        pct_change: parseMaybeNumber(row.pct_change ?? row.change_pct ?? row.percent_change ?? row.percent),
      };
    })
    .filter((row): row is LiveIndex => row !== null);

  return {
    source_url: typeof payload.source_url === "string" ? payload.source_url : "",
    as_of:
      typeof payload.as_of === "string"
        ? payload.as_of
        : typeof payload.snapshot_at === "string"
          ? payload.snapshot_at
          : typeof payload.snapshot_date === "string"
            ? payload.snapshot_date
            : null,
    indices,
  };
};

const RS_LINE_COLORS = [
  "var(--blue-text)",
  "var(--color-success)",
  "#fbbf24",
  "var(--color-danger)",
  "var(--purple-text)",
];

const formatNumber = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });

const formatSignedPercent = (value: number | null | undefined, digits = 2) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
};

const formatSignedPoints = (value: number | null | undefined, digits = 0) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatNumber(value, digits)} pts`;
};

const formatSignedPercentDecimal = (value: number | null | undefined, digits = 2) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  const pct = value * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(digits)}%`;
};


const calcPointChange = (level: number | null | undefined, dailyPct: number | null | undefined) => {
  if (level == null || dailyPct == null || Number.isNaN(level) || Number.isNaN(dailyPct)) return null;
  const pct = dailyPct / 100;
  if (Math.abs(1 + pct) < 1e-9) return null;
  return level - level / (1 + pct);
};

const metricToneClass = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return "is-neutral";
  if (value > 0) return "is-positive";
  if (value < 0) return "is-negative";
  return "is-neutral";
};

const regimeToneClass = (regime: string | null | undefined) => {
  const value = String(regime || "").toLowerCase();
  if (value.includes("distribution")) return "is-negative";
  if (value.includes("weakness")) return "is-negative";
  if (value.includes("base")) return "is-neutral";
  if (value.includes("volatility")) return "is-neutral";
  if (value.includes("narrow")) return "is-neutral";
  if (value.includes("strength") || value.includes("broad")) return "is-positive";
  return "is-neutral";
};

const breadthArrow = (direction: "positive" | "negative" | "neutral") => {
  if (direction === "positive") return "▲";
  if (direction === "negative") return "▼";
  return "•";
};

const driverTypeLabel = (driverType: "contributors" | "detractors" | "mixed") => {
  if (driverType === "contributors") return "contributors";
  if (driverType === "detractors") return "detractors";
  return "drivers";
};

const liquidityTrendLabel = (value: number) => {
  if (value > 0) return "Rising";
  if (value < 0) return "Falling";
  return "Stable";
};

const concentrationInterpretation = (vs3moAvg: number) => {
  if (vs3moAvg > 0.5) return "Elevated";
  if (vs3moAvg < -0.5) return "Low";
  return "Normal";
};

const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

  const concentrationLine = (top5Weight: number, vs3moAvg: number) => {
    if (vs3moAvg > 0) return `Concentration remains elevated at ${formatNumber(top5Weight, 2)}%.`;
    if (vs3moAvg < 0) return `Concentration is below 3-month average at ${formatNumber(top5Weight, 2)}%.`;
    return `Concentration is near 3-month average at ${formatNumber(top5Weight, 2)}%.`;
  };

  const liquidityVerb = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return "was flat";
    if (value > 0) return "rose";
    if (value < 0) return "fell";
    return "was flat";
  };

const scoreToneClass = (score: number) => {
  if (score < 40) return "is-negative";
  if (score < 60) return "is-neutral";
  return "is-positive";
};

const rsStateToneClass = (state: string | null | undefined) => {
  const value = String(state || "").toLowerCase();
  if (value.includes("outperformance")) return "is-positive";
  if (value.includes("underperformance")) return "is-negative";
  return "is-neutral";
};

const regimeShortPhrase = (regime: string) => {
  const value = regime.toLowerCase();
  if (value.includes("distribution")) return "Participation Weak - Liquidity Contracted";
  if (value.includes("weakness")) return "Returns & Breadth Soft";
  if (value.includes("base")) return "Stabilizing Base";
  if (value.includes("narrow")) return "Leadership Concentrated";
  if (value.includes("volatility")) return "High Dispersion Regime";
  if (value.includes("strength") || value.includes("broad")) return "Participation Supportive";
  return regime;
};

const marketHealthBand = (score: number | null | undefined) => {
  const value = Number(score ?? 0);
  if (value < 40) return "Weak";
  if (value < 60) return "Neutral";
  if (value < 80) return "Healthy";
  return "Strong Expansion";
};

const clampPct = (value: number) => Math.max(0, Math.min(100, value));

const formatUsdCompact = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  const abs = Math.abs(value);
  if (abs < 500_000_000) return `$${formatNumber(value / 1_000_000, 2)}m`;
  return `$${formatNumber(value / 1_000_000_000, 2)}bn`;
};

const buildLinePath = (
  values: Array<number | null | undefined>,
  width: number,
  height: number,
  minValue: number,
  maxValue: number
) => {
  const range = Math.max(maxValue - minValue, 1e-9);
  const step = values.length > 1 ? width / (values.length - 1) : width;
  let hasActiveSegment = false;

  return values
    .map((value, index) => {
      if (value == null || Number.isNaN(value)) {
        hasActiveSegment = false;
        return "";
      }

      const x = index * step;
      const y = height - ((value - minValue) / range) * height;
      const command = hasActiveSegment ? "L" : "M";
      hasActiveSegment = true;
      return `${command}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .filter(Boolean)
    .join(" ");
};

const zeroLineY = (height: number, minValue: number, maxValue: number) => {
  if (minValue > 0 || maxValue < 0) return null;
  const range = Math.max(maxValue - minValue, 1e-9);
  return height - ((0 - minValue) / range) * height;
};

export default function HomePage() {
  const [selectedIndex, setSelectedIndex] = useState("KSE-100");
  const [selectedIndexReady, setSelectedIndexReady] = useState(false);
  const [marketIntelLoading, setMarketIntelLoading] = useState(false);
  const [marketIntelError, setMarketIntelError] = useState<string | null>(null);
  const [marketIntel, setMarketIntel] = useState<MarketIntelligencePayload | null>(null);
  const [rsPeers, setRsPeers] = useState<RelativeStrengthPeersPayload | null>(null);
  const [rsPeersLoading, setRsPeersLoading] = useState(false);
  const [rsPeersError, setRsPeersError] = useState<string | null>(null);
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null);
  const [marketStatsLoading, setMarketStatsLoading] = useState(true);
  const [marketStatsError, setMarketStatsError] = useState<string | null>(null);
  const [live, setLive] = useState<LivePayload | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = normalizeIndexSelection(localStorage.getItem("psx_home_selected_index"));
    if (stored) setSelectedIndex(stored);
    setSelectedIndexReady(true);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && selectedIndexReady) {
      localStorage.setItem("psx_home_selected_index", selectedIndex);
    }
  }, [selectedIndex, selectedIndexReady]);

  useEffect(() => {
    if (!selectedIndexReady) return;

    const controller = new AbortController();

    const loadMarketIntelligence = async () => {
      try {
        setMarketIntelLoading(true);
        setMarketIntelError(null);
        const res = await fetch(`/api/market-intelligence?index=${encodeURIComponent(selectedIndex)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as MarketIntelligencePayload;
        setMarketIntel(json);
      } catch (err) {
        if (controller.signal.aborted) return;
        setMarketIntel(null);
        setMarketIntelError(err instanceof Error ? err.message : "Failed to load market intelligence");
      } finally {
        if (!controller.signal.aborted) {
          setMarketIntelLoading(false);
        }
      }
    };
    void loadMarketIntelligence();
    return () => controller.abort();
  }, [selectedIndex, selectedIndexReady]);

  useEffect(() => {
    if (!selectedIndexReady) return;

    const controller = new AbortController();

    const loadRsPeers = async () => {
      try {
        setRsPeersLoading(true);
        setRsPeersError(null);
        const res = await fetch(
          `/api/market-intelligence/index-relative-strength/peers?index=${encodeURIComponent(selectedIndex)}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as RelativeStrengthPeersPayload;
        setRsPeers(json);
      } catch (err) {
        if (controller.signal.aborted) return;
        setRsPeers(null);
        setRsPeersError(err instanceof Error ? err.message : "Failed to load index relative strength");
      } finally {
        if (!controller.signal.aborted) {
          setRsPeersLoading(false);
        }
      }
    };
    void loadRsPeers();
    return () => controller.abort();
  }, [selectedIndex, selectedIndexReady]);

  useEffect(() => {
    const loadMarketStats = async () => {
      try {
        setMarketStatsLoading(true);
        setMarketStatsError(null);
        const res = await fetch("/api/scanner/market?limit=2000&use_snapshot=1");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const rows: ScannerMarketRow[] = Array.isArray(json?.rows) ? json.rows : [];
        const latestDate = json?.latest_date ?? null;
        const snapshotStats = json?.stats ?? null;
        setMarketStats(computeMarketStats(rows, latestDate, snapshotStats));
      } catch (err) {
        setMarketStats(null);
        setMarketStatsError(err instanceof Error ? err.message : "Failed to load market stats");
      } finally {
        setMarketStatsLoading(false);
      }
    };
    loadMarketStats();
  }, []);

  useEffect(() => {
    const loadLive = async () => {
      try {
        const res = await fetch("/api/outputs/indices_eod.json");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setLive(normalizeLivePayload(json));
      } catch {
        setLive(null);
      }
    };
    loadLive();
  }, []);

  const tradedValueSeries = useMemo(() => {
    const raw = marketIntel?.selected_index.traded_value_series_30d;
    if (!Array.isArray(raw)) return [];
    return raw.map((point) => {
      const date = String(point.session_date || "");
      return {
        date,
        shortDate: date.length >= 10 ? date.slice(5) : date,
        tradedUsdBn: (point.traded_value_usd ?? 0) / 1_000_000_000,
        symbolsWithData: point.symbols_with_data,
      };
    });
  }, [marketIntel]);

  const tradedValueChartUnit = useMemo<"m" | "bn">(() => {
    if (tradedValueSeries.length === 0) return "bn";
    const maxBn = Math.max(...tradedValueSeries.map((point) => Math.abs(point.tradedUsdBn)));
    return maxBn < 0.5 ? "m" : "bn";
  }, [tradedValueSeries]);

  const tradedValueChartData = useMemo(() => {
    return tradedValueSeries.map((point) => ({
      ...point,
      tradedUsdDisplay: tradedValueChartUnit === "m" ? point.tradedUsdBn * 1000 : point.tradedUsdBn,
    }));
  }, [tradedValueSeries, tradedValueChartUnit]);

  const tradedValueSvg = useMemo(() => {
    const values = tradedValueChartData.map((point) => point.tradedUsdDisplay).filter((value) => Number.isFinite(value));
    if (values.length === 0) return null;
    const minRaw = Math.min(...values);
    const maxRaw = Math.max(...values);
    const padding = Math.max((maxRaw - minRaw) * 0.12, maxRaw === minRaw ? Math.max(Math.abs(maxRaw) * 0.08, 1) : 0);
    const min = minRaw - padding;
    const max = maxRaw + padding;

    return {
      path: buildLinePath(values, 640, 170, min, max),
      max,
      mid: (min + max) / 2,
      min,
      firstLabel: tradedValueChartData[0]?.shortDate ?? "",
      lastLabel: tradedValueChartData[tradedValueChartData.length - 1]?.shortDate ?? "",
    };
  }, [tradedValueChartData]);

  const moversBarData = useMemo(() => {
    if (!marketIntel) return [];
    const gainers = marketIntel.inputs.top_weighted_contributors.slice(0, TOP_MOVER_COUNT).map((row) => ({
      label: row.symbol,
      side: "Gainer",
      contributionPct: row.contribution_pct,
      returnPct: row.return_pct,
    }));
    const decliners = marketIntel.inputs.top_weighted_detractors.slice(0, TOP_MOVER_COUNT).map((row) => ({
      label: row.symbol,
      side: "Decliner",
      contributionPct: row.contribution_pct,
      returnPct: row.return_pct,
    }));
    return [...gainers, ...decliners];
  }, [marketIntel]);

  const moversMaxAbs = useMemo(() => {
    if (moversBarData.length === 0) return 1;
    return Math.max(...moversBarData.map((row) => Math.abs(row.contributionPct)), 0.01);
  }, [moversBarData]);

  const structured = marketIntel?.structuredMarketIntelligence ?? null;
  const liveIndex = useMemo(() => {
    if (!live?.indices?.length) return null;
    const code = INDEX_CODE_MAP[selectedIndex] || normalizeCode(selectedIndex);
    return (
      live.indices.find(
        (row) =>
          normalizeCode(row.code) === code ||
          normalizeCode(row.index) === code ||
          row.index.toLowerCase() === selectedIndex.toLowerCase()
      ) || null
    );
  }, [live, selectedIndex]);

  const displayLevel = useMemo(() => {
    if (typeof liveIndex?.value === "number" && !Number.isNaN(liveIndex.value) && liveIndex.value > 0) {
      return liveIndex.value;
    }
    if (
      typeof marketIntel?.selected_index.level === "number" &&
      !Number.isNaN(marketIntel.selected_index.level) &&
      marketIntel.selected_index.level > 0
    ) {
      return marketIntel.selected_index.level;
    }
    return null;
  }, [liveIndex, marketIntel]);

  const dailyChangePct = useMemo(() => {
    if (typeof liveIndex?.pct_change === "number" && !Number.isNaN(liveIndex.pct_change)) {
      return liveIndex.pct_change;
    }
    return marketIntel?.selected_index.daily_pct ?? null;
  }, [liveIndex, marketIntel]);

  const dailyPointChange = useMemo(() => {
    if (typeof liveIndex?.change === "number" && !Number.isNaN(liveIndex.change)) {
      return liveIndex.change;
    }
    return calcPointChange(displayLevel, dailyChangePct);
  }, [liveIndex, displayLevel, dailyChangePct]);
  const rsPeersRows = useMemo(() => {
    if (!rsPeers?.pairs?.length) return [];
    const peers = rsPeers.pairs
      .filter((row) => row.benchmarkName !== selectedIndex)
      .map((row) => ({
        ...row,
        benchmarkLabel: INDEX_LABELS[row.benchmarkName] || row.benchmarkName,
      }));
    peers.sort((a, b) => a.benchmarkLabel.localeCompare(b.benchmarkLabel));
    return peers;
  }, [rsPeers, selectedIndex]);

  const rsPeersChart = useMemo(() => {
    if (!rsPeersRows.length) return { data: [] as Array<Record<string, number | string>>, keys: [] as string[] };
    const map = new Map<string, Record<string, number | string>>();
    rsPeersRows.forEach((row) => {
      row.rsLine.forEach((point) => {
        const date = point.date;
        const entry = map.get(date) || { date, shortDate: date.length >= 10 ? date.slice(5) : date };
        entry[row.benchmarkLabel] = point.value;
        map.set(date, entry);
      });
    });
    const data = Array.from(map.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const keys = rsPeersRows.map((row) => row.benchmarkLabel);
    return { data, keys };
  }, [rsPeersRows]);

  const rsPeersSvg = useMemo(() => {
    if (rsPeersChart.data.length === 0 || rsPeersChart.keys.length === 0) return null;
    const allValues = rsPeersChart.keys.flatMap((key) =>
      rsPeersChart.data
        .map((row) => row[key])
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value))
    );
    if (allValues.length === 0) return null;

    const minRaw = Math.min(...allValues);
    const maxRaw = Math.max(...allValues);
    const padding = Math.max((maxRaw - minRaw) * 0.14, maxRaw === minRaw ? Math.max(Math.abs(maxRaw) * 0.08, 0.01) : 0);
    const min = minRaw - padding;
    const max = maxRaw + padding;

    return {
      min,
      max,
      zeroY: zeroLineY(220, min, max),
      firstLabel: String(rsPeersChart.data[0]?.shortDate ?? ""),
      lastLabel: String(rsPeersChart.data[rsPeersChart.data.length - 1]?.shortDate ?? ""),
      lines: rsPeersChart.keys.map((key, idx) => ({
        key,
        color: RS_LINE_COLORS[idx % RS_LINE_COLORS.length],
        path: buildLinePath(
          rsPeersChart.data.map((row) => {
            const value = row[key];
            return typeof value === "number" ? value : null;
          }),
          640,
          220,
          min,
          max
        ),
      })),
    };
  }, [rsPeersChart]);

  const indexOptions = useMemo(() => {
    const source = marketIntel?.available_indices?.length ? marketIntel.available_indices : MARKET_INTELLIGENCE_INDICES;
    return source.filter((value, index, self) => self.indexOf(value) === index);
  }, [marketIntel]);

  const selectedIndexLabel = INDEX_LABELS[selectedIndex] || selectedIndex;

  const topFocalStats = useMemo(() => {
    if (!marketIntel || !structured) return [];

    return [
      {
        label: "Breadth Participation",
        value: `${formatNumber(structured.participation.participationPct, 1)}%`,
        tone:
          structured.participation.breadthDirection === "positive"
            ? "is-positive"
            : structured.participation.breadthDirection === "negative"
              ? "is-negative"
              : "is-neutral",
        detail: `${marketIntel.selected_index.advancers_count} adv / ${marketIntel.selected_index.decliners_count} dec`,
      },
      {
        label: "Liquidity vs 30D",
        value: formatSignedPercent(structured.liquidity.vs30dAvgPct),
        tone: metricToneClass(structured.liquidity.vs30dAvgPct),
        detail: liquidityTrendLabel(structured.liquidity.vs30dAvgPct),
      },
      {
        label: "Traded Value",
        value: formatUsdCompact(marketIntel.selected_index.total_traded_value_usd),
        tone: "is-neutral",
        detail: `Prev ${formatUsdCompact(marketIntel.selected_index.previous_total_traded_value_usd)}`,
      },
      {
        label: "Driver Concentration",
        value: `${formatNumber(structured.driverConcentrationImpact.impactSharePct, 1)}%`,
        tone: metricToneClass(structured.driverConcentrationImpact.top3Impact),
        detail: `${titleCase(structured.driverConcentrationImpact.distributionLabel)} move distribution`,
      },
    ];
  }, [marketIntel, structured]);

  const contributorRows = useMemo(() => {
    if (!marketIntel) return [];
    return [
      ...marketIntel.inputs.top_weighted_contributors.slice(0, 3).map((row) => ({
        ...row,
        side: "Leader" as const,
        tone: "is-positive",
      })),
      ...marketIntel.inputs.top_weighted_detractors.slice(0, 3).map((row) => ({
        ...row,
        side: "Drag" as const,
        tone: "is-negative",
      })),
    ];
  }, [marketIntel]);

  const currentFocusLine = useMemo(() => {
    if (!structured) return "Loading the current market structure read for the selected index.";
    return `${selectedIndexLabel} is showing ${marketHealthBand(structured.marketHealth.score).toLowerCase()} conditions with ${regimeShortPhrase(structured.marketHealth.regimeLabel).toLowerCase()}.`;
  }, [selectedIndexLabel, structured]);

  const marketRead = useMemo(() => {
    if (!marketIntel || !structured) return null;

    const breadthText = `${formatNumber(structured.participation.participationPct, 1)}% participation`;
    const liquidityText = `${formatSignedPercent(structured.liquidity.vs30dAvgPct)} liquidity vs 30D`;
    const driverText = `Top 3 drivers ${formatNumber(structured.driverConcentrationImpact.impactSharePct, 1)}%`;

    if (
      structured.marketHealth.score >= 60 &&
      structured.participation.breadthDirection === "positive" &&
      structured.liquidity.vs30dAvgPct >= -10
    ) {
      return {
        tone: "positive" as const,
        label: "Constructive market",
        action: "Breadth, liquidity, and index structure are supportive. Focus on leaders with confirmation from sector and relative strength.",
        evidence: [breadthText, liquidityText, driverText],
      };
    }

    if (
      structured.marketHealth.score < 40 ||
      structured.participation.breadthDirection === "negative" ||
      structured.liquidity.vs30dAvgPct <= -20
    ) {
      return {
        tone: "caution" as const,
        label: "Defensive market",
        action: "Market quality is weak or liquidity is not supporting the move. Keep position size controlled and avoid chasing.",
        evidence: [breadthText, liquidityText, driverText],
      };
    }

    return {
      tone: "neutral" as const,
      label: "Selective market",
      action: "Signals are mixed. Start with breadth and liquidity, then inspect only stocks with clean relative strength.",
      evidence: [breadthText, liquidityText, driverText],
    };
  }, [marketIntel, structured]);

  return (
    <>
      <section className="page-header page-header--accent market-overview-header">
        <div className="page-header-container page-header-container--workspace">
          <div className="market-overview-header-main">
            <div className="market-overview-header-copy">
              <div className="market-overview-kicker">Market Context</div>
              <h1 className="page-title">Market Overview</h1>
              <p className="page-subtitle">
                A simple read of breadth, liquidity, index drivers, and relative strength before you inspect individual stocks.
              </p>
            </div>

            <div className="market-overview-header-controls">
              <label className="market-overview-index-switcher">
                <span>Index</span>
                <select
                  value={selectedIndex}
                  onChange={(event) => setSelectedIndex(event.target.value)}
                  className="market-overview-index-select"
                >
                  {indexOptions.map((idx) => (
                    <option key={idx} value={idx}>
                      {INDEX_LABELS[idx] || idx}
                    </option>
                  ))}
                </select>
              </label>

              <div className="market-overview-header-pills">
                <span>{marketIntel?.source.latest_session_date ? `Session ${marketIntel.source.latest_session_date}` : "Session syncing"}</span>
                <span>{live?.as_of ? `Live feed ${live.as_of}` : "Live PSX pulse"}</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="strategy-detail-section home-section market-overview-section">
        <div className="strategy-detail-container home-shell">
          {marketIntelLoading && <div className="home-card market-intel-loading">Loading latest market intelligence...</div>}
          {marketIntelError && <div className="home-card market-intel-error">Error: {marketIntelError}</div>}

          {!marketIntelLoading && !marketIntelError && marketIntel && structured && (
            <>
              <nav className="market-overview-section-nav" aria-label="Market overview sections">
                <a href="#market-read">Market Read</a>
                <a href="#breadth">Breadth</a>
                <a href="#liquidity">Liquidity</a>
                <a href="#drivers">Drivers</a>
                <a href="#market-stats">Market Stats</a>
                <a href="#relative-strength">Relative Strength</a>
              </nav>

              {marketRead && (
                <div id="market-read" className={`market-overview-read market-overview-read--${marketRead.tone}`}>
                  <div>
                    <div className="market-overview-read-kicker">Market Read</div>
                    <div className="market-overview-read-title">{marketRead.label}</div>
                    <p>{marketRead.action}</p>
                  </div>
                  <div className="market-overview-read-evidence">
                    {marketRead.evidence.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="market-overview-context-grid">
                <div className="home-card market-overview-context-card">
                  <div className="market-overview-card-head">
                    <div>
                      <div className="market-overview-card-kicker">Session Read</div>
                      <h2>Start with market quality</h2>
                      <p>{currentFocusLine}</p>
                    </div>
                    <div className="market-intel-meta">
                      <span>{selectedIndexLabel}</span>
                      <span>{marketHealthBand(structured.marketHealth.score)}</span>
                      <span>{marketIntel.source.latest_session_date || "No session date"}</span>
                    </div>
                  </div>

                  <div className="market-pulse-strip market-pulse-strip--overview">
                    <div className="market-pulse-primary">
                      <div className="market-pulse-index">{selectedIndexLabel}</div>
                      <div className="market-pulse-level tabular-nums">
                        <strong>{displayLevel != null ? formatNumber(displayLevel) : "N/A"}</strong>
                      </div>
                      <div className={`market-pulse-change ${metricToneClass(dailyChangePct)} tabular-nums`}>
                        <span>{formatSignedPoints(dailyPointChange)}</span>
                        <span className="market-pulse-change-percent">({formatSignedPercent(dailyChangePct)})</span>
                      </div>
                    </div>
                    <div className="market-pulse-health">
                      <div className="market-pulse-label tabular-nums">
                        Market Health: {formatNumber(structured.marketHealth.score, 1)} / 100
                      </div>
                      <div className="market-pulse-health-track">
                        <div
                          className={`market-pulse-health-fill ${scoreToneClass(structured.marketHealth.score)}`}
                          style={{ width: `${clampPct(structured.marketHealth.score)}%` }}
                        />
                      </div>
                    </div>
                    <div className="market-pulse-regime">
                      <span className={`market-pulse-regime-pill ${regimeToneClass(structured.marketHealth.regimeLabel)}`}>
                        {regimeShortPhrase(structured.marketHealth.regimeLabel)}
                      </span>
                    </div>
                  </div>

                  <div className="market-overview-priority-grid">
                    {topFocalStats.map((item) => (
                      <div key={item.label} className="market-overview-priority-card">
                        <span>{item.label}</span>
                        <strong className={`${item.tone} tabular-nums`}>{item.value}</strong>
                        <small>{item.detail}</small>
                      </div>
                    ))}
                  </div>

                  <div className="market-overview-session-note">
                    <strong>Structure Summary</strong>
                    <p>{structured.structuralText}</p>
                  </div>
                </div>

                <div id="breadth" className="home-card market-overview-cluster-card">
                  <div className="market-overview-card-kicker">Breadth</div>
                  <h3>Are more stocks rising?</h3>
                  <div className={`market-overview-cluster-value ${metricToneClass(structured.weightComparison.capReturn)} tabular-nums`}>
                    {formatNumber(structured.participation.participationPct, 1)}% {breadthArrow(structured.participation.breadthDirection)}
                  </div>
                  <p className="market-overview-cluster-copy">
                    Advancers {marketIntel.selected_index.advancers_count}, decliners {marketIntel.selected_index.decliners_count}, unchanged {marketIntel.selected_index.unchanged_count}.
                  </p>
                  <div className="market-overview-mini-grid">
                    <div className="market-overview-mini-stat">
                      <span>Equal-Weighted</span>
                      <strong className={`${metricToneClass(structured.weightComparison.equalReturn)} tabular-nums`}>
                        {formatSignedPercent(structured.weightComparison.equalReturn)}
                      </strong>
                    </div>
                    <div className="market-overview-mini-stat">
                      <span>Cap-Weighted</span>
                      <strong className={`${metricToneClass(structured.weightComparison.capReturn)} tabular-nums`}>
                        {formatSignedPercent(structured.weightComparison.capReturn)}
                      </strong>
                    </div>
                    <div className="market-overview-mini-stat">
                      <span>Divergence</span>
                      <strong className={`${metricToneClass(structured.weightComparison.divergence)} tabular-nums`}>
                        {formatSignedPercent(structured.weightComparison.divergence)}
                      </strong>
                    </div>
                    <div className="market-overview-mini-stat">
                      <span>Status</span>
                      <strong>{structured.weightComparison.status}</strong>
                    </div>
                  </div>
                </div>

                <div id="liquidity" className="home-card market-overview-cluster-card">
                  <div className="market-overview-card-kicker">Liquidity</div>
                  <h3>Is money active?</h3>
                  <div className={`market-overview-cluster-value ${metricToneClass(structured.liquidity.vs30dAvgPct)} tabular-nums`}>
                    {formatUsdCompact(marketIntel.selected_index.total_traded_value_usd)}
                  </div>
                  <p className="market-overview-cluster-copy">
                    Liquidity {liquidityVerb(structured.liquidity.vs30dAvgPct)} {formatSignedPercent(structured.liquidity.vs30dAvgPct)} against the 30-session baseline.
                  </p>
                  <div className="market-overview-mini-grid">
                    <div className="market-overview-mini-stat">
                      <span>vs 30D Avg</span>
                      <strong className={`${metricToneClass(structured.liquidity.vs30dAvgPct)} tabular-nums`}>
                        {formatSignedPercent(structured.liquidity.vs30dAvgPct)}
                      </strong>
                    </div>
                    <div className="market-overview-mini-stat">
                      <span>Trend</span>
                      <strong>{liquidityTrendLabel(structured.liquidity.vs30dAvgPct)}</strong>
                    </div>
                    <div className="market-overview-mini-stat">
                      <span>Coverage</span>
                      <strong className="tabular-nums">
                        {marketIntel.selected_index.constituents_with_data}/{marketIntel.selected_index.constituent_count}
                      </strong>
                    </div>
                    <div className="market-overview-mini-stat">
                      <span>FF Market Cap</span>
                      <strong className="tabular-nums">{formatUsdCompact(marketIntel.selected_index.total_ff_market_cap_usd)}</strong>
                    </div>
                  </div>
                </div>

                <div id="drivers" className="home-card market-overview-cluster-card">
                  <div className="market-overview-card-kicker">Contributors</div>
                  <h3>What moved the index?</h3>
                  <div className="market-overview-driver-list">
                    {contributorRows.map((row) => (
                      <div key={`${row.side}-${row.symbol}`} className="market-overview-driver-row">
                        <div className="market-overview-driver-copy">
                          <strong>{row.symbol}</strong>
                          <span>{row.side} · {row.company}</span>
                        </div>
                        <div className="market-overview-driver-metrics tabular-nums">
                          <span className={row.tone}>{formatSignedPercent(row.contribution_pct)}</span>
                          <span className={metricToneClass(row.return_pct)}>{formatSignedPercent(row.return_pct)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="market-overview-cluster-copy">
                    Top 3 {driverTypeLabel(structured.driverConcentrationImpact.driverType)} drove {formatNumber(structured.driverConcentrationImpact.impactSharePct, 1)}% of the total move.
                  </p>
                </div>
              </div>

              <div className="market-overview-analytics-grid">
                <div id="market-stats" className="home-card market-stats-card market-overview-analytics-card">
                  <div className="market-stats-header">
                    <div>
                      <div className="market-overview-card-kicker">Breadth Extremes</div>
                      <h3 style={{ margin: 0 }}>Market Statistics</h3>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                        Upper and lower circuit counts plus 52-week extremes for a quick breadth stress check.
                      </div>
                    </div>
                    <div className="market-stats-actions">
                      {marketStats?.latestDate && (
                        <span className="market-stats-date">As of {marketStats.latestDate}</span>
                      )}
                      <Link href="/market-overview/market-stats" className="ui-button ui-button-ghost">
                        Detail
                      </Link>
                    </div>
                  </div>

                  {marketStatsLoading && <div className="market-stats-loading">Loading market stats...</div>}
                  {marketStatsError && <div className="market-stats-error">Error: {marketStatsError}</div>}

                  {!marketStatsLoading && !marketStatsError && marketStats && (
                    <div className="market-stats-grid">
                      <div className="market-stats-tile is-positive">
                        <span>Upper Circuit</span>
                        <strong className="tabular-nums">{formatNumber(marketStats.upperCircuit, 0)}</strong>
                      </div>
                      <div className="market-stats-tile is-negative">
                        <span>Lower Circuit</span>
                        <strong className="tabular-nums">{formatNumber(marketStats.lowerCircuit, 0)}</strong>
                      </div>
                      <div className="market-stats-tile is-positive">
                        <span>52 Week High</span>
                        <strong className="tabular-nums">{formatNumber(marketStats.fiftyTwoWeekHigh, 0)}</strong>
                      </div>
                      <div className="market-stats-tile is-negative">
                        <span>52 Week Low</span>
                        <strong className="tabular-nums">{formatNumber(marketStats.fiftyTwoWeekLow, 0)}</strong>
                      </div>
                    </div>
                  )}
                </div>

                <div id="liquidity-trend" className="home-card market-overview-analytics-card">
                  <div className="market-overview-card-kicker">Liquidity Trend</div>
                  <h3>Traded value versus recent baseline</h3>
                  <p className="market-overview-card-copy">
                    Use the 30-session trend to judge whether the current move has enough volume to persist.
                  </p>
                  {tradedValueSeries.length > 0 ? (
                    <div className="market-intel-traded-chart-wrap">
                      <div className="market-intel-traded-chart-head">
                        Traded Value Trend (Last 30 Sessions, USD {tradedValueChartUnit})
                      </div>
                      <div className="market-intel-traded-chart" aria-label="Traded value trend">
                        {tradedValueSvg && (
                          <>
                            <div className="market-intel-line-plot">
                              <div className="market-intel-y-axis" aria-hidden="true">
                                <span>{formatNumber(tradedValueSvg.max, tradedValueChartUnit === "m" ? 0 : 1)}</span>
                                <span>{formatNumber(tradedValueSvg.mid, tradedValueChartUnit === "m" ? 0 : 1)}</span>
                                <span>{formatNumber(tradedValueSvg.min, tradedValueChartUnit === "m" ? 0 : 1)}</span>
                              </div>
                              <svg
                                className="market-intel-line-svg"
                                viewBox="0 0 640 170"
                                preserveAspectRatio="none"
                                role="img"
                                aria-hidden="true"
                              >
                                <line x1="0" y1="28" x2="640" y2="28" />
                                <line x1="0" y1="85" x2="640" y2="85" />
                                <line x1="0" y1="142" x2="640" y2="142" />
                                <path d={tradedValueSvg.path} className="market-intel-line-path" />
                              </svg>
                            </div>
                            <div className="market-intel-chart-axis">
                              <span>{tradedValueSvg.firstLabel}</span>
                              <span>USD {tradedValueChartUnit}</span>
                              <span>{tradedValueSvg.lastLabel}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="market-intel-rs-meta">No traded value series available.</div>
                  )}
                </div>

                <div id="index-drivers" className="home-card market-overview-analytics-card">
                  <div className="market-overview-card-kicker">Index Drivers</div>
                  <h3>Major gainers and decliners</h3>
                  <p className="market-overview-card-copy">
                    This split shows whether the index move came from broad leadership or a narrow heavyweight pocket.
                  </p>
                  {moversBarData.length > 0 ? (
                    <div className="market-intel-movers-chart-wrap">
                      <div className="market-intel-movers-chart-head">Major Gainers & Decliners (Top {TOP_MOVER_COUNT} each)</div>
                      <div className="market-intel-movers-chart market-intel-movers-chart--bars" aria-label="Index driver contributions">
                        {moversBarData.map((row) => {
                          const width = clampPct((Math.abs(row.contributionPct) / moversMaxAbs) * 50);
                          const left = row.contributionPct < 0 ? 50 - width : 50;
                          return (
                            <div key={`${row.side}-${row.label}`} className="market-intel-mover-row">
                              <span className="market-intel-mover-symbol">{row.label}</span>
                              <span className="market-intel-mover-track" aria-hidden="true">
                                <span
                                  className={`market-intel-mover-fill ${row.contributionPct >= 0 ? "is-positive" : "is-negative"}`}
                                  style={{ left: `${left}%`, width: `${width}%` }}
                                />
                              </span>
                              <span className={`market-intel-mover-value ${metricToneClass(row.contributionPct)} tabular-nums`}>
                                {formatSignedPercent(row.contributionPct)}
                              </span>
                              <span className={`market-intel-mover-return ${metricToneClass(row.returnPct)} tabular-nums`}>
                                {formatSignedPercent(row.returnPct)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="market-intel-movers-footnote">
                        Top 3 stocks accounted for {formatNumber(structured.driverConcentrationImpact.impactSharePct, 1)}% of total index move.
                      </div>
                    </div>
                  ) : (
                    <div className="market-intel-rs-meta">No driver contribution chart available.</div>
                  )}
                </div>

                <div className="home-card market-overview-analytics-card market-overview-analytics-card--compact">
                  <div className="market-overview-card-kicker">Risk & Concentration</div>
                  <h3>Leadership concentration check</h3>
                  <ul className="market-intel-quick-summary">
                    <li>
                      Top 5 Weight: <strong className="tabular-nums">{formatNumber(structured.concentration.top5Weight, 2)}%</strong>
                    </li>
                    <li>
                      vs 3M Avg:{" "}
                      <strong className={`${metricToneClass(structured.concentration.vs3moAvg)} tabular-nums`}>
                        {formatSignedPercent(structured.concentration.vs3moAvg)}
                      </strong>
                    </li>
                    <li>
                      Interpretation: <strong>{concentrationInterpretation(structured.concentration.vs3moAvg)}</strong>
                    </li>
                    <li>{concentrationLine(structured.concentration.top5Weight, structured.concentration.vs3moAvg)}</li>
                  </ul>
                </div>

                <div id="relative-strength" className="home-card market-overview-analytics-card market-overview-analytics-card--wide">
                  <div className="market-overview-card-kicker">Relative Strength</div>
                  <h3>Index relative strength versus peers</h3>
                  <p className="market-overview-card-copy">
                    Compare the selected index against other PSX benchmarks before you assume leadership will persist.
                  </p>
                  {rsPeersLoading && (
                    <div className="market-intel-rs-meta">Loading relative strength comparisons...</div>
                  )}
                  {rsPeersError && (
                    <div className="market-intel-rs-error">Error: {rsPeersError}</div>
                  )}
                  {!rsPeersLoading && !rsPeersError && rsPeersRows.length === 0 && (
                    <div className="market-intel-rs-meta">No relative strength comparisons available.</div>
                  )}
                  {!rsPeersLoading && !rsPeersError && rsPeersRows.length > 0 && (
                    <>
                      {rsPeersChart.data.length > 0 && (
                        <div className="market-intel-rs-chart">
                          <div className="market-intel-rs-chart-head">
                            Relative Strength Trend (rebased to start of window)
                          </div>
                          <div className="market-intel-rs-chart-canvas" aria-label="Relative strength trend">
                            {rsPeersSvg && (
                              <>
                                <svg
                                  className="market-intel-line-svg"
                                  viewBox="0 0 640 220"
                                  preserveAspectRatio="none"
                                  role="img"
                                  aria-hidden="true"
                                >
                                  <line x1="0" y1="36" x2="640" y2="36" />
                                  <line x1="0" y1="110" x2="640" y2="110" />
                                  <line x1="0" y1="184" x2="640" y2="184" />
                                  {rsPeersSvg.zeroY != null && (
                                    <line
                                      className="market-intel-zero-line"
                                      x1="0"
                                      y1={rsPeersSvg.zeroY}
                                      x2="640"
                                      y2={rsPeersSvg.zeroY}
                                    />
                                  )}
                                  {rsPeersSvg.lines.map((line) => (
                                    <path key={line.key} d={line.path} className="market-intel-line-path" style={{ stroke: line.color }} />
                                  ))}
                                </svg>
                                <div className="market-intel-chart-axis">
                                  <span>{rsPeersSvg.firstLabel}</span>
                                  <span>
                                    {formatSignedPercentDecimal(rsPeersSvg.min)} to {formatSignedPercentDecimal(rsPeersSvg.max)}
                                  </span>
                                  <span>{rsPeersSvg.lastLabel}</span>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="market-intel-rs-legend">
                            {rsPeersChart.keys.map((key, idx) => (
                              <span key={key}>
                                <i style={{ background: RS_LINE_COLORS[idx % RS_LINE_COLORS.length] }} />
                                {key}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="market-intel-rs-table">
                        <div className="market-intel-rs-row market-intel-rs-head">
                          <span>Benchmark</span>
                          <span>RS 1M</span>
                          <span>RS 2M</span>
                          <span>RS 3M</span>
                          <span>State</span>
                        </div>
                        {rsPeersRows.map((row) => (
                          <div key={`${row.indexName}-${row.benchmarkName}`} className="market-intel-rs-row">
                            <span>{row.benchmarkLabel}</span>
                            <span className={`${metricToneClass(row.windows.RS_30D)} tabular-nums`}>{formatSignedPercentDecimal(row.windows.RS_30D)}</span>
                            <span className={`${metricToneClass(row.windows.RS_60D)} tabular-nums`}>{formatSignedPercentDecimal(row.windows.RS_60D)}</span>
                            <span className={`${metricToneClass(row.windows.RS_90D)} tabular-nums`}>{formatSignedPercentDecimal(row.windows.RS_90D)}</span>
                            <span className={rsStateToneClass(row.state)}>{row.state}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

    </>
  );
}
